import React from 'react';
import PropTypes from 'prop-types';
import { METRIC_LABELS } from './Constants';
import { InfoDot } from './InfoUI';
import { OVERVIEW, LENS_WIKI, wikiArticle } from './wikiContent';
import { tip } from './Tip';

const NAVY = '#14536e';
const AXES = ['contract', 'pressure', 'development', 'movement', 'consequence',
  'economy', 'assembly', 'clarity', 'freshness', 'unpredictability', 'charge', 'grip'];

// The wiki article for whatever the wheel is coloured by — so the (i) next to "Colour by"
// always opens a real guide (the overview for Triage/D&E, the lens guide for a lens, the
// axis guide for a single axis).
function metricArticle(metric) {
  if (metric === 'overall' || metric === 'de') return { ...OVERVIEW.framework, kicker: 'How to read' };
  if (LENS_WIKI[metric]) return { ...LENS_WIKI[metric], kicker: 'Lens' };
  const a = wikiArticle('axis', metric);
  return a ? { ...a, kicker: 'Axis' } : null;
}

export default function Controls({ includeScenes, onToggle, metric, onMetricChange,
  view = 'sunburst', onViewChange, dimNotOwed = true, onToggleDim, onOpenWiki, prominentScenes = false }) {
  const showDim = AXES.includes(metric);   // only single axes can be "not owed" in a unit
  const scenesToggleStyle = prominentScenes
    ? {
        flexBasis: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        border: `2px solid ${includeScenes ? NAVY : '#b7cbd6'}`,
        background: includeScenes ? '#e7f0f5' : '#fff',
        borderRadius: 8,
        padding: '8px 11px',
        boxShadow: '0 2px 8px rgba(20,83,110,.10)',
      }
    : undefined;
  const scenesInputStyle = prominentScenes ? { width: 20, height: 20, marginTop: 0, flexShrink: 0 } : undefined;
  const scenesLabelStyle = prominentScenes
    ? { color: NAVY, fontWeight: 800, fontSize: '.98rem', lineHeight: 1.2 }
    : undefined;
  return (
    <div className="flex-grow-1">
      {/* Row 1 — how you view the structure: the wheel or the table. */}
      {onViewChange && (
        <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="btn-group btn-group-sm" role="group" aria-label="Unit view" id="sos-view-toggle">
            {[['sunburst', 'Sunburst'], ['table', 'Table']].map(([v, label]) => {
              const active = view === v;
              return (
                <button key={v} type="button" onClick={() => onViewChange(v)}
                  style={{ fontWeight: 600, padding: '.25rem .8rem', border: `1.5px solid ${active ? NAVY : '#cbd5e1'}`,
                           background: active ? NAVY : '#fff', color: active ? '#fff' : NAVY }}>{label}</button>
              );
            })}
          </div>
        </div>
      )}

      {/* Row 2 — sunburst-only options. Hidden in Table. */}
      {view === 'sunburst' && (
        <div className="d-flex align-items-center mt-2" style={{ gap: 18, flexWrap: 'wrap' }}>
          <div className="form-check m-0" style={scenesToggleStyle}>
            <input className="form-check-input" type="checkbox" id="toggleScenes" checked={includeScenes} onChange={onToggle} style={scenesInputStyle} />
            <label className="form-check-label fw-medium" htmlFor="toggleScenes" style={scenesLabelStyle}>Include Scenes</label>
          </div>
          <div className="d-flex align-items-center">
            <label htmlFor="metric-select" style={{ fontWeight: 600, color: NAVY, fontSize: 13, marginRight: 8, whiteSpace: 'nowrap' }}>Colour by</label>
            <select id="metric-select" className="form-select form-select-sm w-auto metric-select-enhanced"
                    value={metric} onChange={e => onMetricChange(e.target.value)}
                    style={{ borderColor: NAVY, color: NAVY, fontWeight: 600, cursor: 'pointer' }}>
              <option value="overall">{METRIC_LABELS.overall}</option>
              <optgroup label="Groups">
                {['de', 'design', 'execution', 'read'].map(k => <option key={k} value={k}>{METRIC_LABELS[k]}</option>)}
              </optgroup>
              <optgroup label="Axes">
                {AXES.map(k => <option key={k} value={k}>{METRIC_LABELS[k]}</option>)}
              </optgroup>
            </select>
            {/* (i) → opens the full guide for whatever's selected, in the right-side wiki drawer */}
            <span style={{ marginLeft: 6, display: 'inline-flex' }}>
              <InfoDot article={metricArticle(metric)} onOpen={onOpenWiki} size={20} />
            </span>
          </div>

          {/* Dim not-owed: makes the neutral-greying explicit and controllable (axes only). */}
          {showDim && (
            <div className="form-check m-0" {...tip('Grey out units where this axis is only lightly relevant, so the real problems stand out.')}>
              <input className="form-check-input" type="checkbox" id="toggleDim"
                     checked={dimNotOwed} onChange={onToggleDim} />
              <label className="form-check-label fw-medium" htmlFor="toggleDim">Grey out where not owed</label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

Controls.propTypes = {
  includeScenes: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  metric: PropTypes.string.isRequired,
  onMetricChange: PropTypes.func.isRequired,
  view: PropTypes.string,
  onViewChange: PropTypes.func,
  dimNotOwed: PropTypes.bool,
  onToggleDim: PropTypes.func,
  onOpenWiki: PropTypes.func,
  prominentScenes: PropTypes.bool,
};
